import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    DRAFT_BULK_UPLOAD_VALIDATION_KEY,
    DraftsValidationSummaryItem,
    useIsFeatureSupported,
} from '@steelbuy/data-access';
import { useModal } from '@steelbuy/modal-dialog';
import { Feature } from '@steelbuy/ts-shared';
import {
    ButtonTertiaryOnLightM,
    Notification,
    NotificationLevel,
    toaster,
    useWizardStepsContext,
    WizardBarItemStatus,
} from '@steelbuy/ui-primitive';
import { useIsVendorFeatureSupported } from '@steelbuy/vendor';
import { DeleteBulkListingModal } from './DeleteBulkListingModal';
import { DeleteBulkListingsModal } from './DeleteBulkListingsModal';
import { EditModal, SUCCESSFUL_TOAST_ID } from './EditModal';
import { RFQTeaser } from '../../create-rfq/rfq-teaser/RFQTeaser';
import { WizardActionBar } from '../../wizard-action-bar/WizardActionBar';
import { useBulkListingContext } from '../bulk-listing-context/BulkListingContext';
import { BulkListingTable } from '../bulk-listing-table/BulkListingTable';
import { BulkListingStep } from '../BulkListing.enums';
import {
    BulkListing,
    getFilteredHeadings,
    getIncompleteListings,
    getListingsGrouped,
    getStepRoute,
    GroupedListings,
} from '../bulkListingUtil';

import './EditListing.scss';

const LISTINGS_FIELD = 'listings';

const getIndexForListing = (groupedListings: GroupedListings[], tableIndex: number, rowIndex: number) => {
    let index = 0;
    for (let i = 0; i < tableIndex; i += 1) {
        index += groupedListings[i].listings.length;
    }
    return index + rowIndex;
};

const getListingByIndex = (groupedListings: GroupedListings[], index: number) => {
    let counter = 0;
    for (let i = 0; i < groupedListings.length; i += 1) {
        for (let j = 0; j < groupedListings[i].listings.length; j += 1) {
            if (counter === index) {
                return groupedListings[i].listings[j];
            }
            counter += 1;
        }
    }
    return null;
};

type BulkDeleteButtonProps = {
    label: string;
    onSubscribe: (listener: (count: number) => void) => () => void;
    onClick: () => void;
};

const BulkDeleteButton = ({ label, onSubscribe, onClick }: BulkDeleteButtonProps) => {
    const [selectionCount, setSelectionCount] = useState(0);

    useEffect(() => {
        const unsubscribe = onSubscribe(setSelectionCount);
        return unsubscribe;
    }, [onSubscribe]);

    return <ButtonTertiaryOnLightM label={label} onClick={onClick} disabled={selectionCount === 0} />;
};

export const EditListingsStep = () => {
    const { draftId = '' } = useParams();
    const navigate = useNavigate();
    const [selectedListingIndex, setSelectedListingIndex] = useState(0);
    const [editFormKey, setEditFormKey] = useState(0);
    const { state } = useLocation();

    // Use a ref for the selected IDs — checkbox changes don't need to re-render
    // this component. Only the BulkDeleteButton needs to know about count changes,
    // and it subscribes via a lightweight listener pattern.
    //
    // selectedListingIdsByTableRef tracks selections per-table index so that
    // checking rows in one table does not wipe out selections in another table.
    const selectedListingIdsByTableRef = useRef<Map<number, string[]>>(new Map());
    const selectionListeners = useRef<Set<(count: number) => void>>(new Set());
    const [selectionResetKey, setSelectionResetKey] = useState(0);

    const getAllSelectedIds = useCallback((): string[] => {
        const all: string[] = [];
        selectedListingIdsByTableRef.current.forEach((ids) => all.push(...ids));
        return all;
    }, []);

    const notifySelectionListeners = useCallback(() => {
        const total = getAllSelectedIds().length;
        selectionListeners.current.forEach((fn) => fn(total));
    }, [getAllSelectedIds]);

    const subscribeToSelection = useCallback(
        (listener: (count: number) => void) => {
            selectionListeners.current.add(listener);
            // initialise with current count
            listener(getAllSelectedIds().length);
            return () => {
                selectionListeners.current.delete(listener);
            };
        },
        [getAllSelectedIds]
    );

    const editModal = useModal();
    const deleteModal = useModal();
    const bulkDeleteModal = useModal();
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain']);
    const { updateStepStatus } = useWizardStepsContext();
    const { control, getValues, reset, setValue } = useFormContext();
    const { fields, remove } = useFieldArray({
        name: LISTINGS_FIELD,
        control,
    });
    const {
        handleSaveDraft,
        updateBulkUploadMutation: { isLoading },
        addEditedListing,
        addDeletedListing,
        addDeletedListings,
        clearValidationError,
        clearValidationErrors,
        setShowUploadCompleteNotification,
        showUploadCompleteNotification,
    } = useBulkListingContext();
    const showUploadNotification = useRef(showUploadCompleteNotification);
    const isQualityCategoriesSupported = useIsVendorFeatureSupported('NON_PRIME_CATEGORIES');
    const isPriceOptional = useIsFeatureSupported(Feature.IS_PRICE_OPTIONAL);
    const isDraftSupported = useIsFeatureSupported(Feature.LISTING_DRAFT);

    // Subscribe reactively to the validation cache so that clearing a validation error
    // (via setQueryData) causes a re-render and updates the invalid row highlighting.
    const { data: validationSummary } = useQuery<DraftsValidationSummaryItem[]>({
        queryKey: [DRAFT_BULK_UPLOAD_VALIDATION_KEY, draftId],
        enabled: false, // never fetches — only reads from cache
    });
    const invalidIds = useMemo(() => new Set((validationSummary ?? []).map((item) => item.id)), [validationSummary]);

    const listingsData = fields as BulkListing[];
    const formData = getValues();

    const groupedListings = useMemo(() => getListingsGrouped(listingsData), [fields]);

    const { incompleteListingsCount, reviewCount } = useMemo(() => {
        const incompleteListings = getIncompleteListings(
            listingsData as BulkListing[],
            isQualityCategoriesSupported,
            isPriceOptional
        );
        const count = new Set([...incompleteListings.map((item) => item.id), ...[...invalidIds]]).size;
        return {
            incompleteListingsCount: incompleteListings.length,
            reviewCount: count,
        };
    }, [fields, isQualityCategoriesSupported, isPriceOptional, invalidIds]);

    const isNextDisabled = incompleteListingsCount !== 0 || invalidIds.size > 0;

    useEffect(() => {
        if (listingsData.length === 0 && !draftId) {
            reset();
            updateStepStatus(BulkListingStep.EDIT_LISTINGS, WizardBarItemStatus.EMPTY);
            navigate(getStepRoute(1), { replace: true });
        }
        setShowUploadCompleteNotification(false);
    }, [listingsData.length]);

    const onUpdate = useCallback(
        (listingId: string, updatedData: BulkListing) => {
            const currentValues = getValues(LISTINGS_FIELD) || [];
            const newValues = [...currentValues];
            const index = currentValues.findIndex((listing: { listingId: string }) => listing.listingId === listingId);
            newValues[index] = updatedData;
            setValue(LISTINGS_FIELD, newValues, { shouldValidate: true, shouldDirty: true });
        },
        [control, getValues, setValue]
    );

    const handleSelectionChange = useCallback(
        (tableIndex: number, updatedIds: string[]) => {
            selectedListingIdsByTableRef.current.set(tableIndex, updatedIds);
            notifySelectionListeners();
        },
        [notifySelectionListeners]
    );

    const handleBulkDeleteConfirm = useCallback(() => {
        const selectedIds = getAllSelectedIds();
        const indicesToRemove = selectedIds
            .map((id) => listingsData.findIndex((l) => l.listingId === id))
            .filter((i) => i !== -1)
            .sort((a, b) => b - a);

        indicesToRemove.forEach((i) => remove(i));
        addDeletedListings(selectedIds);
        clearValidationErrors(draftId, selectedIds);
        selectedListingIdsByTableRef.current.clear();
        notifySelectionListeners();
        setSelectionResetKey((k) => k + 1);
        bulkDeleteModal.hide();
        toaster.success(
            t('application.createBulkListing.editListings.bulkListingsDeleted', { count: selectedIds.length })
        );
    }, [
        getAllSelectedIds,
        listingsData,
        remove,
        addDeletedListings,
        clearValidationErrors,
        draftId,
        bulkDeleteModal,
        t,
        notifySelectionListeners,
    ]);

    if (listingsData.length === 0) {
        return null;
    }

    const bulkDeleteLabel = t('application.createBulkListing.editListings.bulkDeleteLabel');

    return (
        <div className="bulk-listing-import">
            <p className="bulk-listing-import-listing__title edit-listing-step__title">
                {t('application.createBulkListing.editListings.header', { count: listingsData.length })}
            </p>
            {showUploadNotification.current && (
                <Notification
                    closeButton
                    stayOpen
                    level={NotificationLevel.SUCCESS}
                    message={t('application.createBulkListing.editListings.successMessage')}
                />
            )}
            <Notification
                stayOpen
                level={NotificationLevel.INFO}
                message={t('application.createBulkListing.editListings.summaryMessage', {
                    uploadedCount: listingsData.length,
                    incompleteCount: reviewCount,
                })}
            />
            <div className="edit-listing-step__bulk-delete">
                <BulkDeleteButton
                    label={bulkDeleteLabel}
                    onSubscribe={subscribeToSelection}
                    onClick={bulkDeleteModal.show}
                />
            </div>
            {groupedListings.map((listingGroup, tableIndex) => (
                <div className="bulk-listing-import-group-item" key={listingGroup.groupValue}>
                    <RFQTeaser
                        material={{ ...listingGroup.listings[0], [listingGroup.groupedBy]: listingGroup.groupValue }}
                        showQuality={!isQualityCategoriesSupported}
                    />
                    <BulkListingTable
                        listingsData={listingGroup.listings}
                        invalidIds={invalidIds}
                        onRowClick={(rowIndex) => {
                            setSelectedListingIndex(getIndexForListing(groupedListings, tableIndex, rowIndex));
                            setEditFormKey((k) => k + 1);
                            toast.dismiss(SUCCESSFUL_TOAST_ID);
                            editModal.show();
                        }}
                        showCompleteness
                        tableHeadings={getFilteredHeadings({ listings: listingGroup.listings })}
                        onDelete={(rowIndex) => {
                            setSelectedListingIndex(getIndexForListing(groupedListings, tableIndex, rowIndex));
                            deleteModal.show();
                        }}
                        showSelectionCheckboxes
                        selectionResetKey={selectionResetKey}
                        onSelectionChange={(updatedIds) => handleSelectionChange(tableIndex, updatedIds)}
                    />
                </div>
            ))}
            <div className="edit-listing-step__bulk-delete">
                <BulkDeleteButton
                    label={bulkDeleteLabel}
                    onSubscribe={subscribeToSelection}
                    onClick={bulkDeleteModal.show}
                />
            </div>
            <WizardActionBar
                onNext={() => {
                    updateStepStatus(BulkListingStep.EDIT_LISTINGS, WizardBarItemStatus.SUCCESS);
                    navigate(getStepRoute(3, draftId), {
                        state: {
                            fromWizard: true,
                            ...state,
                            showSuccessNotification: false,
                        },
                    });
                }}
                isSubmitDisabled={isNextDisabled}
                nextLabel={t('application.createRFQ.wizardNextButtonLabel')}
                isLoading={isLoading}
                onBack={() => {
                    updateStepStatus(BulkListingStep.EDIT_LISTINGS, WizardBarItemStatus.EMPTY);
                    navigate(getStepRoute(1), { replace: true });
                }}
                onSaveDraft={isDraftSupported ? () => handleSaveDraft(draftId) : undefined}
            />
            <EditModal
                modalAccessor={editModal}
                index={selectedListingIndex}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                materialData={getListingByIndex(groupedListings, selectedListingIndex) as any}
                update={onUpdate}
                onNext={() => {
                    const nextIndex = selectedListingIndex === listingsData.length - 1 ? 0 : selectedListingIndex + 1;
                    setSelectedListingIndex(nextIndex);
                    setEditFormKey((k) => k + 1);
                }}
                totalCount={listingsData.length}
                onDelete={() => {
                    editModal.hide();
                    deleteModal.show();
                }}
                isLastListing={selectedListingIndex === listingsData.length - 1}
                bulkUploadFormData={formData}
                addEditedListing={addEditedListing}
                onViewedListing={(listingId) => {
                    clearValidationError(draftId, listingId);
                    setEditFormKey((k) => k + 1);
                }}
                formKey={editFormKey}
            />
            <DeleteBulkListingModal
                modalAccessor={deleteModal}
                onConfirm={() => {
                    const listingForIndex = getListingByIndex(groupedListings, selectedListingIndex);
                    if (listingForIndex === null) {
                        return;
                    }
                    const index = listingsData.findIndex((listing) => listing.listingId === listingForIndex.listingId);
                    remove(index);
                    addDeletedListing(listingForIndex.listingId);
                    clearValidationError(draftId, listingForIndex.listingId);
                    // Remove the deleted listing from the cross-table selection map so the
                    // bulk-delete button count stays accurate after a single-row delete.
                    selectedListingIdsByTableRef.current.forEach((ids, tableIndex) => {
                        const filtered = ids.filter((id) => id !== listingForIndex.listingId);
                        if (filtered.length !== ids.length) {
                            selectedListingIdsByTableRef.current.set(tableIndex, filtered);
                        }
                    });
                    notifySelectionListeners();
                    deleteModal.hide();
                    toaster.success(t('application.createBulkListing.editListings.listingDeleted'));
                }}
            />
            <DeleteBulkListingsModal modalAccessor={bulkDeleteModal} onConfirm={handleBulkDeleteConfirm} />
        </div>
    );
};
